/**
 * JWT token validation and decoding utilities
 */

export interface JWTPayload {
  exp: number; // Expiration time (seconds since epoch)
  iat?: number; // Issued at (seconds since epoch) - not set by backend
  sub?: string; // Subject (usually user email)
  member_id?: number; // Member ID for DB operations
  member_uuid?: string; // Member UUID for feature gating
  is_active?: boolean; // Activation status at token issuance
  [key: string]: unknown;
}

/**
 * Decode a base64url string to a regular string.
 * JWTs use base64url encoding which differs from standard base64:
 * - Uses '-' instead of '+'
 * - Uses '_' instead of '/'
 * - May omit padding '=' characters
 */
function base64UrlDecode(str: string): string {
  // Replace base64url characters with standard base64 characters
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed (base64 strings must be divisible by 4)
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }

  return atob(base64);
}

/**
 * Decode a JWT token without verification
 * Returns null if token is invalid format
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part) using base64url decoding
    const payload = parts[1];
    const decoded = base64UrlDecode(payload);
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * Returns true if expired or invalid
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // Invalid token is considered expired
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp <= currentTime;
}

/**
 * Get time until token expiration in seconds
 * Returns 0 if expired or invalid
 */
export function getTimeUntilExpiry(token: string): number {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return 0;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = payload.exp - currentTime;
  return Math.max(0, timeRemaining);
}

/**
 * Validate JWT token format and expiration
 * Returns validation result with details
 */
export function validateToken(token: string): {
  valid: boolean;
  expired: boolean;
  expiresIn: number;
  payload: JWTPayload | null;
} {
  const payload = decodeJWT(token);

  if (!payload) {
    return {
      valid: false,
      expired: true,
      expiresIn: 0,
      payload: null,
    };
  }

  const expired = isTokenExpired(token);
  const expiresIn = getTimeUntilExpiry(token);

  return {
    valid: !expired,
    expired,
    expiresIn,
    payload,
  };
}
